import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import './propertyDetails.css';

const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
});

const getProfitability = (row) => Number(row.totalRecordedNet) || 0;
const getNetProfitLoss = (row) => getProfitability(row) - (Number(row.totalInvestment) || 0);

const InvestmentsPage = () => {
    const navigate = useNavigate();
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');

    const aggregates = (report?.rows || []).reduce((acc, row) => {
        acc.totalInvestment += Number(row.totalInvestment) || 0;
        acc.profitability += getProfitability(row);
        acc.netProfitLoss += getNetProfitLoss(row);
        return acc;
    }, { totalInvestment: 0, profitability: 0, netProfitLoss: 0 });

    const loadReport = async () => {
        setLoading(true);
        setLoadError('');
        try {
            const response = await api.get('/report/investments-summary');
            if (response && response.data) {
                setReport(response.data);
            } else {
                setReport(null);
            }
        } catch (error) {
            console.error('failed to load investments summary', error);
            setReport(null);
            setLoadError('Unable to load investments overview right now.');
        } finally {
            setLoading(false);
        }
    };

    const hasRows = Array.isArray(report?.rows) && report.rows.length > 0;

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        loadReport();
    }, []);

    return (
        <div className="property-pnl-page property-investment-page">
            <div className="property-pnl-toolbar">
                <div className="property-pnl-title-block">
                    <h2>Investments Overview</h2>
                    <p>All properties with investment count and total capital invested.</p>
                </div>
                <button type="button" className="btn-primary" onClick={loadReport}>Reload</button>
            </div>

            {loading ? (
                <div className="app-state-card app-state-info">
                    <p>Loading investments overview...</p>
                </div>
            ) : loadError ? (
                <div className="app-state-card app-state-error" role="alert">
                    <p>{loadError}</p>
                    <div>
                        <button type="button" className="btn-primary" onClick={loadReport}>Retry</button>
                    </div>
                </div>
            ) : report && hasRows ? (
                <>
                    <div className="investment-summary-grid">
                        <div className="property-pnl-card investment-summary-card">
                            <span className="investment-summary-label">Properties</span>
                            <strong>{report.propertyCount || 0}</strong>
                        </div>
                        <div className="property-pnl-card investment-summary-card">
                            <span className="investment-summary-label">Investments</span>
                            <strong>{report.totalInvestmentCount || 0}</strong>
                        </div>
                        <div className="property-pnl-card investment-summary-card">
                            <span className="investment-summary-label">Total Invested</span>
                            <strong>{currencyFormatter.format(Number(report.totalInvestmentAmount) || 0)}</strong>
                        </div>
                        <div className="property-pnl-card investment-summary-card">
                            <span className="investment-summary-label">Portfolio Net P/L</span>
                            <strong className={aggregates.netProfitLoss >= 0 ? 'net-positive' : 'net-negative'}>
                                {currencyFormatter.format(aggregates.netProfitLoss)}
                            </strong>
                        </div>
                    </div>

                    <div className="property-pnl-card">
                        <div className="property-pnl-table-wrap">
                            <table className="pnl-table property-investment-table investment-overview-table">
                                <thead>
                                    <tr>
                                        <th>Property</th>
                                        <th>Location</th>
                                        <th>Profitability</th>
                                        <th>Total Investment</th>
                                        <th>Net Profit/Loss</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {report.rows.map((row) => (
                                        <tr key={row.propertyId}>
                                            <td>{row.propertyName}</td>
                                            <td>{[row.address1, row.city, row.state].filter(Boolean).join(', ')}</td>
                                            <td className={getProfitability(row) >= 0 ? 'net-positive' : 'net-negative'}>
                                                {currencyFormatter.format(getProfitability(row))}
                                            </td>
                                            <td>{currencyFormatter.format(Number(row.totalInvestment) || 0)}</td>
                                            <td className={getNetProfitLoss(row) >= 0 ? 'net-positive' : 'net-negative'}>
                                                {currencyFormatter.format(getNetProfitLoss(row))}
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="btn-secondary btn-inline"
                                                    onClick={() => navigate(`/PropertyDetails/${row.propertyId}/investments`)}
                                                >
                                                    View Property Report
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                                        <td style={{ fontWeight: 700 }}>Aggregate</td>
                                        <td style={{ fontWeight: 700 }}>All Properties</td>
                                        <td className={aggregates.profitability >= 0 ? 'net-positive' : 'net-negative'} style={{ fontWeight: 700 }}>
                                            {currencyFormatter.format(aggregates.profitability)}
                                        </td>
                                        <td style={{ fontWeight: 700 }}>
                                            {currencyFormatter.format(aggregates.totalInvestment)}
                                        </td>
                                        <td className={aggregates.netProfitLoss >= 0 ? 'net-positive' : 'net-negative'} style={{ fontWeight: 700 }}>
                                            {currencyFormatter.format(aggregates.netProfitLoss)}
                                        </td>
                                        <td style={{ fontWeight: 700 }}>-</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            ) : report ? (
                <div className="app-state-card">
                    <p>No investments found yet. Add investment entries on each property to build this portfolio report.</p>
                    <div>
                        <button type="button" className="btn-primary" onClick={() => navigate('/Properties')}>Go to Properties</button>
                    </div>
                </div>
            ) : (
                <div className="app-state-card">
                    <p>No investment report is available yet.</p>
                </div>
            )}
        </div>
    );
};

export default InvestmentsPage;